import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { wsfTerminalsApiMeta } from "../apiMeta";
import {
  type TerminalSailingSpaceByTerminalIdInput,
  terminalSailingSpaceByTerminalIdInputSchema,
} from "./shared/terminalSailingSpace.input";
import {
  type TerminalSailingSpace,
  terminalSailingSpaceSchema,
} from "./shared/terminalSailingSpace.output";

/**
 * Metadata for the fetchTerminalSailingSpaceByTerminalId endpoint
 */
export const terminalSailingSpaceByTerminalIdMeta = {
  functionName: "fetchTerminalSailingSpaceByTerminalId",
  endpoint: "/terminalSailingSpace/{TerminalID}",
  inputSchema: terminalSailingSpaceByTerminalIdInputSchema,
  outputSchema: terminalSailingSpaceSchema,
  sampleParams: { TerminalID: 7 },
  endpointDescription:
    "Get sailing space availability for a specific terminal by ID.",
} satisfies EndpointMeta<
  TerminalSailingSpaceByTerminalIdInput,
  TerminalSailingSpace
>;

/**
 * Fetch function for retrieving sailing space availability for a specific terminal by ID
 */
const { fetch, useHook } = createFetchAndHook<
  TerminalSailingSpaceByTerminalIdInput,
  TerminalSailingSpace
>({
  api: wsfTerminalsApiMeta,
  endpoint: terminalSailingSpaceByTerminalIdMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/terminalSailingSpace.endpoints").terminalSailingSpaceGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving sailing space availability for a specific terminal by ID
 */
export const fetchTerminalSailingSpaceByTerminalId: FetchFactory<
  TerminalSailingSpaceByTerminalIdInput,
  TerminalSailingSpace
> = fetch;

/**
 * React Query hook for retrieving sailing space availability for a specific terminal by ID
 */
export const useTerminalSailingSpaceByTerminalId: HookFactory<
  TerminalSailingSpaceByTerminalIdInput,
  TerminalSailingSpace
> = useHook;
